import { shuffle } from 'lodash';
import { playableChatPreviews } from './chatPreviewQueue';
import { previewTopicKey } from './previewTopic';

const previewsInGroup = group => Array.isArray(group?.previews) ? group.previews : [];

export const TOPIC_OPENING_PREVIEW_COUNT = 8;

export const topicPreviewsFromGroups = (topicId, topicGroups, excludedIds = []) => {
    const topicKey = previewTopicKey(topicId);
    if (!topicKey) return [];
    const excluded = new Set(excludedIds);
    return playableChatPreviews((Array.isArray(topicGroups) ? topicGroups : [])
        .filter(group => previewTopicKey(group?.id) === topicKey)
        .flatMap(previewsInGroup))
        .filter(preview => !excluded.has(preview.id));
};

// The selected clip is already playing. Fill the eight-clip topic opening, then
// alternate shuffled related-topic pools so one large pool cannot dominate.
export const buildTopicPreviewContinuation = (topicId, primaryPreviews, topicGroups, excludedIds = [], openingSlots = TOPIC_OPENING_PREVIEW_COUNT - 1) => {
    const topicKey = previewTopicKey(topicId);
    const seen = new Set(excludedIds);
    const primary = playableChatPreviews(Array.isArray(primaryPreviews) ? primaryPreviews : [])
        .filter(preview => !seen.has(preview.id));
    const opening = primary.slice(0, Math.max(0, openingSlots));
    opening.forEach(preview => seen.add(preview.id));

    // Remaining primary clips can qualify through another topic, but the
    // selected topic does not get its own pool after the opening.
    const groups = new Map();
    (Array.isArray(topicGroups) ? topicGroups : []).forEach(group => {
        const key = previewTopicKey(group?.id);
        if (!key || key === topicKey) return;
        groups.set(key, (groups.get(key) || []).concat(previewsInGroup(group)));
    });

    const pools = [];
    groups.forEach(previews => {
        const unique = playableChatPreviews(previews).filter(preview => {
            if (seen.has(preview.id)) return false;
            seen.add(preview.id);
            return true;
        });
        if (unique.length) pools.push(unique);
    });

    const shuffledPools = shuffle(pools).map(pool => shuffle(pool));
    const mixed = [];
    const rounds = Math.max(0, ...shuffledPools.map(pool => pool.length));
    for (let round = 0; round < rounds; round += 1) {
        shuffledPools.forEach(pool => {
            if (pool[round]) mixed.push(pool[round]);
        });
    }
    return opening.concat(mixed);
};
